import React, { useEffect } from "react";
import styled from "styled-components";
import {
  generateVisit,
  getProfileVisualizer,
  getProfileData,
} from "../../store/actions";
import PerfilMain from "../../components/Perfil/PerfilMain/PerfilMain.tsx";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import RegisteredUserProfileLoader from "./RegisteredUsersSkeleton/RegisteredUserProfileLoader";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { fireAnalytics } from "../../utils/firebase";
import {
  getSessionId,
  getDeviceCategory,
  getPreviousPage,
} from "../../utils/analyticsHelpers.ts";

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  height: 100%;
`;

interface UserProfileData {
  id?: string | number;
  [key: string]: unknown;
}

interface RegisteredUserProfileStateProps {
  userData: UserProfileData;
  profileVisualizerData?: UserProfileData | null;
  loading?: boolean;
}

interface RegisteredUserProfileDispatchProps {
  generateVisit: (userId: number) => void;
  getProfileVisualizer: (userId: string) => void;
  getProfileData: (userId: string) => void;
}

type RegisteredUserProfileProps = RegisteredUserProfileStateProps &
  RegisteredUserProfileDispatchProps;

const RegisteredUserProfile = ({
  generateVisit,
  getProfileVisualizer,
  profileVisualizerData,
  getProfileData,
  loading,
  userData,
}: RegisteredUserProfileProps) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const { id } = useParams<{ id: string }>();

  const userId = id === String(userData?.id) ? String(userData.id) : id ?? "";

  const myPerson = userId === String(userData?.id);

  useEffect(() => {
    if (!myPerson) {
      generateVisit(parseInt(userId, 10));
      fireAnalytics("profile_view", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        source: getPreviousPage() ? "navigation" : "direct",
        previous_page: getPreviousPage(),
        device_category: getDeviceCategory(),
        category: "profile",
        action: "profile_view",
        visited_user_id: userId || "",
        is_own_profile: false,
      });
    } else {
      fireAnalytics("profile_view", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        source: getPreviousPage() ? "navigation" : "direct",
        previous_page: getPreviousPage(),
        device_category: getDeviceCategory(),
        category: "profile",
        action: "profile_view",
        visited_user_id: userId || "",
        is_own_profile: true,
      });
    }
  }, [userId, userData, myPerson, generateVisit]);

  useEffect(() => {
    if (!myPerson) {
      getProfileVisualizer(userId);
    } else {
      getProfileData(userId);
    }
  }, [userId, myPerson, getProfileVisualizer, getProfileData]);

  return (
    <Container>
      {ocultarEnMobile ? (
        <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />
      ) : null}
      {loading ? (
        <RegisteredUserProfileLoader
          smallVersion={false}
          noBackground={false}
        />
      ) : (
        <PerfilMain
          perfilAjeno={userId}
          profileVisualizerData={profileVisualizerData}
          myPerson={myPerson}
        />
      )}
    </Container>
  );
};

interface RootState {
  general?: {
    userData?: UserProfileData;
    profileVisualizer?: UserProfileData | null;
    componentLoading?: boolean;
    [key: string]: unknown;
  };
}

const mapStateToProps = (state: RootState): RegisteredUserProfileStateProps => ({
  userData: state.general?.userData ?? ({} as UserProfileData),
  profileVisualizerData: state.general?.profileVisualizer,
  loading: state.general?.componentLoading,
});

export default connect(mapStateToProps, {
  generateVisit,
  getProfileVisualizer,
  getProfileData,
})(RegisteredUserProfile);
